import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../../services/supabaseClient';
import SignatureModule from '../components/documents/SignatureModule';
import Header from '../../../components/Header';
import { 
  ArrowLeft, FileSignature, ShieldCheck, CheckCircle, 
  AlertTriangle, Download, Clock, User, Building, Lock, Loader2, Printer, Shield 
} from 'lucide-react';

export default function LeaseAgreement() {
  const { appId } = useParams<{ appId: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);
  const [leasePdf, setLeasePdf] = useState<any>(null);
  const [userRole, setUserRole] = useState<'tenant' | 'landlord' | null>(null);

  useEffect(() => {
    if (appId) {
      fetchLeaseData();
    }
  }, [appId]);

  const fetchLeaseData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/login?type=habitat');
        return;
      }

      // 1. Récupérer l'application et les infos du logement
      const { data: appData, error: appError } = await supabase
        .from('habitat_applications')
        .select(`*, listing:habitat_listings(*)`)
        .eq('id', appId)
        .single();

      if (appError) throw appError;
      
      setData(appData);
      setUserRole(user.id === appData.tenant_id ? 'tenant' : 'landlord');

      // 2. Récupérer le document PDF du bail
      const { data: leaseData } = await supabase
        .from('habitat_leases')
        .select('*')
        .eq('application_id', appId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (leaseData) {
        setLeasePdf(leaseData);
      }

      // ✅ Redirection automatique vers la page de succès si les deux ont signé
      if (appData.tenant_signed_at && appData.landlord_signed_at) {
        setTimeout(() => {
          navigate(`/habitat/success/${appId}`);
        }, 2500); // Délai de 2.5s
      }

    } catch (error) {
      console.error("Erreur de chargement du bail:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center">
        <Loader2 className="text-teal-500 w-12 h-12 animate-spin mb-4" />
        <p className="text-slate-300 font-bold tracking-widest uppercase text-sm">Chargement de la salle de signature...</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-slate-50 pt-32 px-4 text-center">
        <AlertTriangle className="text-red-500 w-16 h-16 mx-auto mb-4" />
        <h2 className="text-2xl font-black text-slate-900">Dossier introuvable</h2>
        <button onClick={() => navigate(-1)} className="mt-6 text-teal-600 font-bold hover:underline">Retourner au tableau de bord</button>
      </div>
    );
  }
  
  const tenantSigned = !!data.tenant_signed_at;
  const landlordSigned = !!data.landlord_signed_at;
  const isFullySigned = tenantSigned && landlordSigned;

  return (
    <div className="min-h-screen bg-slate-50 font-sans pb-20 selection:bg-teal-500 selection:text-white">
      <Header />

      <main className="max-w-5xl mx-auto px-4 pt-32 pb-16">
        
        {/* Actions ToolBar */}
        <div className="flex items-center justify-between mb-8 print:hidden">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-slate-500 hover:text-teal-600 font-bold transition bg-white px-5 py-2.5 rounded-full border border-slate-200 shadow-sm">
            <ArrowLeft size={16} /> Tableau de bord
          </button>
          <div className="flex gap-3">
            <button onClick={() => window.print()} className="bg-white border border-slate-200 text-slate-600 px-5 py-2.5 rounded-full text-xs font-black uppercase tracking-widest flex items-center gap-2 shadow-sm hover:shadow-md transition">
              <Printer size={16}/> Imprimer
            </button>
            <div className="bg-slate-900 text-white px-5 py-2.5 rounded-full text-xs font-black uppercase tracking-widest flex items-center gap-2 shadow-md">
              <Lock size={14} className="text-teal-400" /> Espace Sécurisé
            </div>
          </div>
        </div>

        {/* Bannière d'état de la signature (Caché à l'impression) */}
        <div className={`print:hidden rounded-[2rem] p-8 md:p-10 text-white shadow-xl mb-10 relative overflow-hidden border ${isFullySigned ? 'bg-gradient-to-br from-green-600 to-teal-700 border-green-500' : 'bg-slate-900 border-slate-800'}`}>
          <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="flex items-center gap-6">
              <div className={`w-20 h-20 rounded-3xl flex items-center justify-center shrink-0 border ${isFullySigned ? 'bg-white/20 border-white/30' : 'bg-teal-500/20 border-teal-500/50'}`}>
                {isFullySigned ? <ShieldCheck size={40} className="text-white" /> : <FileSignature size={40} className="text-teal-400" />}
              </div>
              <div>
                <p className={`font-black text-[10px] uppercase tracking-widest mb-1 ${isFullySigned ? 'text-green-200' : 'text-teal-400'}`}>
                  {isFullySigned ? 'Processus Terminé' : 'En attente de signatures'}
                </p>
                <h1 className="text-3xl md:text-4xl font-black tracking-tight mb-2">Bail Officiel</h1>
                <p className="text-slate-300 font-medium text-sm md:text-base">
                  {data.listing?.title} • {data.listing?.monthly_rent} $ / mois
                </p>
              </div>
            </div>
            
            {/* Indicateurs de progression des signatures */}
            <div className="flex gap-4 shrink-0 w-full md:w-auto bg-black/20 p-4 rounded-2xl border border-white/10">
              <div className="flex flex-col items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 transition-colors ${tenantSigned ? 'bg-green-500 text-white' : 'bg-slate-800 text-slate-500'}`}>
                  {tenantSigned ? <CheckCircle size={20} /> : <User size={20} />}
                </div>
                <span className="text-[10px] font-black uppercase tracking-widest">Locataire</span>
              </div>
              <div className="w-8 h-px bg-white/20 mt-5"></div>
              <div className="flex flex-col items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 transition-colors ${landlordSigned ? 'bg-green-500 text-white' : 'bg-slate-800 text-slate-500'}`}>
                  {landlordSigned ? <CheckCircle size={20} /> : <Building size={20} />}
                </div>
                <span className="text-[10px] font-black uppercase tracking-widest">Propriétaire</span>
              </div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8 print:block">
          
          {/* Colonne de gauche : Le Document PDF & Contrat */}
          <div className="lg:col-span-2 space-y-6 print:space-y-0">
            
            {/* Affichage Iframe si un PDF existe */}
            {leasePdf && (
              <div className="bg-white rounded-[2rem] border border-slate-200 shadow-sm p-4 overflow-hidden h-[800px] flex flex-col print:hidden mb-8">
                <div className="flex justify-between items-center px-4 pb-4 border-b border-slate-100 mb-4">
                  <h3 className="font-black text-slate-900 flex items-center gap-2">Annexe Forfeo & Bail du TAL</h3>
                  <a href={leasePdf.file_url} download className="text-xs bg-slate-100 text-slate-600 px-4 py-2 rounded-xl font-bold hover:bg-slate-200 transition flex items-center gap-2">
                    <Download size={14}/> Télécharger PDF
                  </a>
                </div>
                <iframe 
                  src={`${leasePdf.file_url}#toolbar=0&navpanes=0&scrollbar=0`} 
                  className="w-full flex-1 rounded-xl bg-slate-100 border border-slate-200"
                  title="Bail Forfeo"
                />
              </div>
            )}

            {/* BAIL PHYSIQUE (A4) - Modèle conservé */}
            <div className="bg-white shadow-2xl p-12 md:p-16 rounded-[2rem] border border-slate-200 print:shadow-none print:border-none print:p-0 relative overflow-hidden">
              
              {/* Tampon de Signature */}
              {isFullySigned && (
                <div className="absolute top-10 right-[-40px] rotate-12 bg-green-100 border-4 border-double border-green-600 text-green-600 px-10 py-2 font-black text-xl uppercase tracking-tighter opacity-80 pointer-events-none animate-in zoom-in duration-500">
                  CONTRAT SCELLÉ
                </div>
              )}

              <div className="border-b-4 border-slate-900 pb-6 mb-10">
                <h1 className="text-3xl font-black text-slate-900 uppercase tracking-tighter">Bail de Logement Forfeo</h1>
                <p className="text-slate-500 font-bold uppercase text-[10px] tracking-widest mt-1">Conforme aux normes du Québec • Réf: {data.id.substring(0,8)}</p>
              </div>

              <div className="space-y-10">
                <section>
                  <h3 className="bg-slate-900 text-white px-3 py-1 text-[10px] font-black uppercase inline-block mb-4 italic">A. Les Parties</h3>
                  <div className="grid grid-cols-2 gap-12">
                    <div>
                      <p className="text-[10px] font-black text-slate-400 uppercase mb-2 tracking-widest">Locateur</p>
                      <p className="font-black text-lg text-slate-900 uppercase tracking-tight">{data.listing?.landlord_name || 'Propriétaire'}</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-black text-slate-400 uppercase mb-2 tracking-widest">Locataire</p>
                      <p className="font-black text-lg text-slate-900 uppercase tracking-tight">{data.tenant_name}</p>
                    </div>
                  </div>
                </section>

                <section>
                  <h3 className="bg-slate-900 text-white px-3 py-1 text-[10px] font-black uppercase inline-block mb-4 italic">B. Le Logement</h3>
                  <p className="text-xl font-black text-slate-900 mb-1">{data.listing?.address}</p>
                  <p className="text-slate-500 font-bold">{data.listing?.city}, Québec</p>
                </section>

                <section>
                  <h3 className="bg-slate-900 text-white px-3 py-1 text-[10px] font-black uppercase inline-block mb-4 italic">C. Loyer</h3>
                  <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100 flex items-center justify-between">
                    <div>
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Montant Mensuel</p>
                      <p className="text-3xl font-black text-teal-600">{data.listing?.monthly_rent} $</p>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Date d'effet</p>
                      <p className="text-lg font-black text-slate-900">{data.listing?.available_from}</p>
                    </div>
                  </div>
                </section>

                {/* BLOC SIGNATURES VISUELLES */}
                <section className="pt-8 border-t-2 border-slate-100">
                  <div className="grid grid-cols-2 gap-8">
                    {/* Signature Locateur */}
                    <div>
                      <p className="text-[10px] font-black text-slate-400 uppercase mb-4">Signature du Locateur</p>
                      {data.landlord_signed_at ? (
                        <div className="bg-teal-50 border border-teal-100 p-4 rounded-xl">
                          <p className="text-teal-700 font-black text-sm uppercase">Signé numériquement</p>
                          <p className="text-[10px] text-teal-600 font-bold mt-1">Le {new Date(data.landlord_signed_at).toLocaleString('fr-CA')}</p>
                          <p className="text-[9px] text-teal-500/50 font-mono mt-1">IP: {data.landlord_signature_ip || 'Sécurisée'}</p>
                        </div>
                      ) : (
                        <div className="h-16 border-b border-dashed border-slate-200"></div>
                      )}
                    </div>

                    {/* Signature Locataire */}
                    <div>
                      <p className="text-[10px] font-black text-slate-400 uppercase mb-4">Signature du Locataire</p>
                      {data.tenant_signed_at ? (
                        <div className="bg-teal-50 border border-teal-100 p-4 rounded-xl">
                          <p className="text-teal-700 font-black text-sm uppercase">Signé numériquement</p>
                          <p className="text-[10px] text-teal-600 font-bold mt-1">Le {new Date(data.tenant_signed_at).toLocaleString('fr-CA')}</p>
                          <p className="text-[9px] text-teal-500/50 font-mono mt-1">IP: {data.tenant_signature_ip || 'Sécurisée'}</p>
                        </div>
                      ) : (
                        <div className="h-16 border-b border-dashed border-slate-200"></div>
                      )}
                    </div>
                  </div>
                </section>
              </div>
              
              <div className="mt-12 text-center opacity-30 flex items-center justify-center gap-2">
                <Shield size={12}/>
                <p className="text-[8px] font-black uppercase tracking-[0.2em]">Certifié par Forfeo Habitat Québec 2026</p>
              </div>
            </div>
          </div>

          {/* Colonne de droite : Panneau de Signature Actif */}
          <div className="space-y-6 print:hidden">
            <div className="bg-white rounded-[2rem] border border-slate-200 shadow-sm p-8 sticky top-32">
              <h3 className="text-2xl font-black text-slate-900 mb-6">Signature Requise</h3>

              {isFullySigned ? (
                <div className="bg-green-50 border border-green-200 p-6 rounded-2xl text-center">
                  <ShieldCheck className="text-green-500 w-16 h-16 mx-auto mb-4" />
                  <h4 className="font-black text-green-900 text-lg mb-2">Bail scellé !</h4>
                  <p className="text-sm text-green-800 font-medium mb-6">Toutes les parties ont signé. Le document a une pleine valeur juridique.</p>
                  <p className="text-[10px] font-black uppercase text-green-600 tracking-widest animate-pulse">Redirection en cours...</p>
                </div>
              ) : (
                <>
                  {userRole === 'tenant' && !data.tenant_signed_at && (
                    <SignatureModule appId={data.id} role="tenant" onSigned={fetchLeaseData} />
                  )}
                  {userRole === 'landlord' && !data.landlord_signed_at && (
                    <SignatureModule appId={data.id} role="landlord" onSigned={fetchLeaseData} />
                  )}
                  
                  {/* Messages d'attente */}
                  {userRole === 'tenant' && data.tenant_signed_at && !data.landlord_signed_at && (
                    <div className="bg-slate-50 border border-slate-200 p-6 rounded-2xl text-center">
                      <Clock className="text-teal-500 w-12 h-12 mx-auto mb-3" />
                      <h4 className="font-black text-slate-900 mb-1">Votre signature est enregistrée</h4>
                      <p className="text-xs text-slate-500 font-medium">En attente de la signature du propriétaire.</p>
                    </div>
                  )}
                  
                  {userRole === 'landlord' && data.landlord_signed_at && !data.tenant_signed_at && (
                    <div className="bg-slate-50 border border-slate-200 p-6 rounded-2xl text-center">
                      <Clock className="text-teal-500 w-12 h-12 mx-auto mb-3" />
                      <h4 className="font-black text-slate-900 mb-1">Votre signature est enregistrée</h4>
                      <p className="text-xs text-slate-500 font-medium">En attente de la signature du locataire.</p>
                    </div>
                  )}
                </>
              )}

              {/* Ligne d'audit (Audit Trail) */}
              <div className="mt-8 pt-6 border-t border-slate-100">
                <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-4">Journal d'audit (Horodatage)</h4>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3 text-xs">
                    <div className="w-1.5 h-1.5 rounded-full bg-slate-300 mt-1.5"></div>
                    <div>
                      <p className="font-bold text-slate-700">Document généré</p>
                      {leasePdf ? (
                        <p className="text-slate-400">{new Date(leasePdf.created_at).toLocaleString('fr-CA')}</p>
                      ) : (
                        <p className="text-slate-400 italic">En attente</p>
                      )}
                    </div>
                  </li>
                  <li className="flex items-start gap-3 text-xs">
                    <div className={`w-1.5 h-1.5 rounded-full mt-1.5 ${tenantSigned ? 'bg-teal-500 shadow-[0_0_8px_rgba(20,184,166,0.5)]' : 'bg-slate-200'}`}></div>
                    <div>
                      <p className={`font-bold ${tenantSigned ? 'text-slate-900' : 'text-slate-400'}`}>Signature Locataire</p>
                      {tenantSigned && <p className="text-teal-600 font-medium">{new Date(data.tenant_signed_at).toLocaleString('fr-CA')}</p>}
                    </div>
                  </li>
                  <li className="flex items-start gap-3 text-xs">
                    <div className={`w-1.5 h-1.5 rounded-full mt-1.5 ${landlordSigned ? 'bg-teal-500 shadow-[0_0_8px_rgba(20,184,166,0.5)]' : 'bg-slate-200'}`}></div>
                    <div>
                      <p className={`font-bold ${landlordSigned ? 'text-slate-900' : 'text-slate-400'}`}>Signature Propriétaire</p>
                      {landlordSigned && <p className="text-teal-600 font-medium">{new Date(data.landlord_signed_at).toLocaleString('fr-CA')}</p>}
                    </div>
                  </li>
                </ul>
              </div>

            </div>
          </div>

        </div>
      </main>
    </div>
  );
}
